import { Suspense, lazy } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { Landing } from './Landing';
import { Signin } from './Signin';
import { type ActorPermissions, getPermissions, getRole } from './api';
import { SkeletonScreen } from './components/ui/Skeleton';
import { OwnerHome } from './owner/OwnerHome';
import { OwnerLayout } from './owner/OwnerLayout';
import { OwnerLogin } from './owner/OwnerLogin';
import { StaffLayout } from './staff/StaffLayout';
import { StaffLogin } from './staff/StaffLogin';
import { StaffToday } from './staff/StaffToday';

/**
 * Everything past the first screen is loaded when it is opened.
 *
 * One bundle meant a housekeeper on a phone downloaded the payroll screen, the
 * money screen and the audit log before she could see today's work — 850KB to
 * answer "what am I doing this morning". These are the screens nobody is
 * looking at on arrival.
 *
 * Landing, both layouts and the sign-in form stay in the first chunk: they are
 * the first paint, and code-splitting the thing somebody is already waiting for
 * only adds a round trip.
 */
const AcceptInvite = lazy(() =>
  import('./AcceptInvite').then((m) => ({ default: m.AcceptInvite })),
);
const ActivityLog = lazy(() =>
  import('./owner/ActivityLog').then((m) => ({ default: m.ActivityLog })),
);
const EventDetail = lazy(() =>
  import('./components/EventDetail').then((m) => ({ default: m.EventDetail })),
);
const HouseRules = lazy(() =>
  import('./components/HouseRules').then((m) => ({ default: m.HouseRules })),
);
const NotificationsPage = lazy(() =>
  import('./components/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const Onboarding = lazy(() =>
  import('./owner/Onboarding').then((m) => ({ default: m.Onboarding })),
);
const OperationDetail = lazy(() =>
  import('./owner/OperationDetail').then((m) => ({ default: m.OperationDetail })),
);
const OwnerMoney = lazy(() =>
  import('./owner/OwnerMoney').then((m) => ({ default: m.OwnerMoney })),
);
const OwnerPayroll = lazy(() =>
  import('./owner/OwnerPayroll').then((m) => ({ default: m.OwnerPayroll })),
);
const OwnerPulse = lazy(() =>
  import('./owner/OwnerPulse').then((m) => ({ default: m.OwnerPulse })),
);
const OwnerRoutines = lazy(() =>
  import('./owner/OwnerRoutines').then((m) => ({ default: m.OwnerRoutines })),
);
const OwnerSignup = lazy(() =>
  import('./owner/OwnerSignup').then((m) => ({ default: m.OwnerSignup })),
);
const OwnerStaff = lazy(() =>
  import('./owner/OwnerStaff').then((m) => ({ default: m.OwnerStaff })),
);
const OwnerTasks = lazy(() =>
  import('./owner/OwnerTasks').then((m) => ({ default: m.OwnerTasks })),
);
const PersonPage = lazy(() =>
  import('./owner/PersonPage').then((m) => ({ default: m.PersonPage })),
);
const Profile = lazy(() => import('./Profile').then((m) => ({ default: m.Profile })));
const PayslipPrint = lazy(() =>
  import('./PayslipPrint').then((m) => ({ default: m.PayslipPrint })),
);
const Search = lazy(() => import('./components/Search').then((m) => ({ default: m.Search })));
const StaffAcceptInvite = lazy(() =>
  import('./StaffAcceptInvite').then((m) => ({ default: m.StaffAcceptInvite })),
);
const StaffHistory = lazy(() =>
  import('./staff/StaffHistory').then((m) => ({ default: m.StaffHistory })),
);
const StaffMore = lazy(() => import('./staff/StaffMore').then((m) => ({ default: m.StaffMore })));
const StaffPay = lazy(() => import('./staff/StaffPay').then((m) => ({ default: m.StaffPay })));
const StaffReceipt = lazy(() =>
  import('./staff/StaffReceipt').then((m) => ({ default: m.StaffReceipt })),
);
const WeeklyPlan = lazy(() =>
  import('./owner/WeeklyPlan').then((m) => ({ default: m.WeeklyPlan })),
);

/**
 * What a route shows while its chunk is arriving. The skeleton the screens
 * already use, so a slow connection reads as loading rather than as broken.
 */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<SkeletonScreen rows={3} />}>{children}</Suspense>;
}

function RequireOwner() {
  const role = getRole();
  if (role !== 'owner') return <Navigate to="/login" replace />;
  return <Outlet />;
}
function RequireStaff() {
  const role = getRole();
  if (role !== 'staff') return <Navigate to="/staff/login" replace />;
  return <Outlet />;
}
function RequireAny() {
  const role = getRole();
  if (!role) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Open to the owner, and to a staff member the owner trusted with this
 * permission.
 *
 * These three screens used to sit behind RequireOwner, which is why granting
 * "hand out work" to the house manager changed nothing she could see. The
 * server has always enforced the same permission on every call underneath
 * them, so this guard decides what to offer, not what to allow.
 */
function RequirePermission({ need }: { need: keyof ActorPermissions }) {
  const role = getRole();
  if (!role) return <Navigate to="/" replace />;
  if (role === 'owner') return <Outlet />;
  if (!getPermissions()[need]) return <Navigate to="/today" replace />;
  return <Outlet />;
}

function RoleAwareLayout() {
  const role = getRole();
  if (role === 'staff') return <StaffLayout />;
  return <OwnerLayout />;
}

export const router = createBrowserRouter([
  { path: '/', element: <Landing /> },
  // Unified sign-in. /login + /staff/login both render the same form,
  // which auto-detects the role from the identifier. The old per-role
  // screens stay registered as fallbacks during the rollout in case any
  // bookmark/email link still points at them.
  { path: '/signin', element: <Signin /> },
  { path: '/login', element: <Signin /> },
  { path: '/_login-owner', element: <OwnerLogin /> },
  {
    path: '/signup',
    element: (
      <Lazy>
        <OwnerSignup />
      </Lazy>
    ),
  },
  { path: '/staff/login', element: <Signin /> },
  { path: '/_login-staff', element: <StaffLogin /> },
  {
    path: '/invite/:token',
    element: (
      <Lazy>
        <AcceptInvite />
      </Lazy>
    ),
  },
  {
    path: '/staff-invite/:token',
    element: (
      <Lazy>
        <StaffAcceptInvite />
      </Lazy>
    ),
  },
  {
    element: <RequireOwner />,
    children: [
      // Onboarding lives outside the OwnerLayout so the nav tabs don't
      // distract from the intake.
      {
        path: '/onboarding',
        element: (
          <Lazy>
            <Onboarding />
          </Lazy>
        ),
      },
      {
        element: <OwnerLayout />,
        children: [
          { path: '/home', element: <OwnerHome /> },
          // Absorbed into the composer, which the round + opens. Kept as a
          // redirect: the address is in browser histories and old push payloads.
          { path: '/quick', element: <Navigate to="/tasks?new=1" replace /> },
          {
            path: '/routines',
            element: (
              <Lazy>
                <OwnerRoutines />
              </Lazy>
            ),
          },
          // The same routines, laid out as the week instead of a list. A
          // second view, not a second feature — writing a cell here creates
          // an ordinary weekly routine.
          {
            path: '/routines/week',
            element: (
              <Lazy>
                <WeeklyPlan />
              </Lazy>
            ),
          },
          {
            path: '/pulse',
            element: (
              <Lazy>
                <OwnerPulse />
              </Lazy>
            ),
          },
          {
            path: '/payroll',
            element: (
              <Lazy>
                <OwnerPayroll />
              </Lazy>
            ),
          },
          // Same screen as the staff /rules below — the owner and the
          // administrative PA are editing the same house rules, so they get
          // the same UI rather than two that drift apart.
          {
            path: '/memory',
            element: (
              <Lazy>
                <HouseRules />
              </Lazy>
            ),
          },
          // The audit trail, which has been written to since migration 0000
          // and read by nobody. Owner-only: every row carries an IP address.
          {
            path: '/activity',
            element: (
              <Lazy>
                <ActivityLog />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  // Tasks, the team and the money are not owner-only screens — they are the
  // three permissions the owner can hand out. Same components for both roles,
  // inside whichever layout the viewer belongs to.
  {
    element: <RequirePermission need="assign_work" />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: [
          {
            path: '/tasks',
            element: (
              <Lazy>
                <OwnerTasks />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <RequirePermission need="manage_staff" />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: [
          {
            path: '/staff',
            element: (
              <Lazy>
                <OwnerStaff />
              </Lazy>
            ),
          },
          // A person's own page — everything about them in one place, which is
          // what a card on People now opens onto.
          {
            path: '/staff/:id',
            element: (
              <Lazy>
                <PersonPage />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <RequirePermission need="see_money" />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: [
          {
            path: '/money',
            element: (
              <Lazy>
                <OwnerMoney />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <RequireStaff />,
    children: [
      {
        element: <StaffLayout />,
        children: [
          { path: '/today', element: <StaffToday /> },
          {
            path: '/today/receipt',
            element: (
              <Lazy>
                <StaffReceipt />
              </Lazy>
            ),
          },
          {
            path: '/history',
            element: (
              <Lazy>
                <StaffHistory />
              </Lazy>
            ),
          },
          {
            path: '/pay',
            element: (
              <Lazy>
                <StaffPay />
              </Lazy>
            ),
          },
          // The house rules. Readable by everyone in the household; the
          // add/edit controls only appear for someone with `assign_work`.
          {
            path: '/rules',
            element: (
              <Lazy>
                <HouseRules />
              </Lazy>
            ),
          },
          // Everything real but not daily, one tap behind the fourth tab.
          {
            path: '/more',
            element: (
              <Lazy>
                <StaffMore />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  // Routes available to both roles — operation detail + profile.
  {
    element: <RequireAny />,
    children: [
      {
        element: <RoleAwareLayout />,
        children: [
          {
            path: '/op/:id',
            element: (
              <Lazy>
                <OperationDetail />
              </Lazy>
            ),
          },
          // The evening a task belongs to. Read by both roles — a housekeeper
          // with one job for Thursday should be able to see what Thursday is.
          // Adding and removing is gated on assign_work inside the screen,
          // and on the server.
          {
            path: '/event/:id',
            element: (
              <Lazy>
                <EventDetail />
              </Lazy>
            ),
          },
          {
            path: '/profile',
            element: (
              <Lazy>
                <Profile />
              </Lazy>
            ),
          },
          // The inbox as a place. The bell it used to live behind is in a
          // header that only exists on a laptop, so on a phone every
          // notification this app has ever written was unreadable.
          {
            path: '/notifications',
            element: (
              <Lazy>
                <NotificationsPage />
              </Lazy>
            ),
          },
          // One box over the whole house. Cmd-K, or "/" from anywhere.
          {
            path: '/search',
            element: (
              <Lazy>
                <Search />
              </Lazy>
            ),
          },
          // A payslip somebody can keep. Printed by the browser, which already
          // shapes Hebrew, Urdu and Tagalog correctly and needs no embedded
          // fonts — and never leaves a copy of somebody's pay in storage.
          {
            path: '/payslip/:id',
            element: (
              <Lazy>
                <PayslipPrint />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
]);
